import { Bike, Pencil, UserX } from "lucide-react";
import type { PartnerDriverRow, PartnerOrderDetail } from "../../lib/partnerOrders";
import {
  driverPresenceTranslationKey,
  partnerDriverPresence,
} from "../../lib/partnerDrivers";
import { useI18n, type TranslationKey } from "../../i18n";

interface OrderAssignDriverProps {
  order: PartnerOrderDetail;
  drivers: PartnerDriverRow[];
  loading?: boolean;
  busy?: boolean;
  fallbackEnabled?: boolean;
  showFallback?: boolean;
  /** Opens the dispatch picker (same modal as the card action). */
  onChangeDriver?: () => void;
  /** Clears the assigned driver (order returns to the ready pool). */
  onUnassignDriver?: () => Promise<void> | void;
  onRequestOelox?: () => Promise<void>;
}

function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .join("") || "DR"
  );
}

/** Translate raw driver status values from the API (fallback path). */
function driverStatusLabel(
  status: string | null,
  t: (key: TranslationKey) => string
): string | null {
  if (!status) return null;
  const value = status.toLowerCase();
  if (value === "busy" || value === "accepted" || value === "picked_up") {
    return t(driverPresenceTranslationKey("busy"));
  }
  if (value === "offline") return t(driverPresenceTranslationKey("offline"));
  if (value === "available" || value === "online") {
    return t(driverPresenceTranslationKey("online"));
  }
  return null;
}

export function OrderAssignDriver({
  order,
  drivers,
  loading,
  busy,
  fallbackEnabled,
  showFallback,
  onChangeDriver,
  onUnassignDriver,
  onRequestOelox,
}: OrderAssignDriverProps) {
  const { t } = useI18n();

  if (order.fulfillment_type !== "delivery") return null;
  if (order.stage === "cancelled" || order.stage === "completed") return null;
  if (order.stage === "pending") return null;

  const canEdit =
    order.stage === "ready" ||
    (order.stage === "in_delivery" && !order.assigned_driver_id);
  if (!canEdit && !order.assigned_driver_id) return null;

  const driverRow = drivers.find((d) => d.id === order.assigned_driver_id) ?? null;
  const presence = driverRow ? partnerDriverPresence(driverRow) : null;
  const presenceLabel = presence
    ? t(driverPresenceTranslationKey(presence))
    : driverStatusLabel(order.driver_status, t);
  const assignedName = order.driver_name ?? t("orders.assign");
  const driverPhone = driverRow?.phone ?? null;

  return (
    <section className="order-assign" aria-label={t("orders.assignSection")}>
      <p className="order-assign__title">
        <Bike className="me-1.5 inline h-3.5 w-3.5" />
        {t("orders.assignSection")}
      </p>

      {loading ? (
        <div className="h-14 rounded-xl skeleton" />
      ) : order.assigned_driver_id ? (
        <div className="order-assign__card">
          <span className="order-assign__avatar" aria-hidden>
            {initialsOf(assignedName)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="order-assign__name truncate">{assignedName}</p>
            <p className="order-assign__meta truncate">
              {driverPhone ? <span dir="ltr">{driverPhone}</span> : null}
              {driverPhone && presenceLabel ? " · " : null}
              {presenceLabel}
            </p>
          </div>
          {canEdit && onChangeDriver ? (
            <button
              type="button"
              className="btn-secondary order-assign__change"
              disabled={busy}
              onClick={onChangeDriver}
            >
              <Pencil className="h-3.5 w-3.5" />
              {t("orders.detail.changeDriver")}
            </button>
          ) : null}
          {canEdit && onUnassignDriver ? (
            <button
              type="button"
              className="order-assign__unlink"
              title={t("orders.clearDriver")}
              aria-label={t("orders.clearDriver")}
              disabled={busy}
              onClick={() => void onUnassignDriver()}
            >
              <UserX className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : drivers.length === 0 ? (
        <p className="order-assign__empty">{t("orders.noDrivers")}</p>
      ) : (
        <p className="order-assign__empty">
          {t("orders.detail.noDriverAssigned")}
        </p>
      )}

      {canEdit &&
      fallbackEnabled &&
      showFallback &&
      onRequestOelox &&
      !order.assigned_driver_id ? (
        <button
          type="button"
          className="btn-secondary mt-3 min-h-[44px] w-full"
          disabled={busy}
          onClick={() => void onRequestOelox()}
        >
          {t("orders.requestOelox")}
        </button>
      ) : null}
    </section>
  );
}
